import { z } from 'zod';
import '@apexdesigner/schema-tools';
import { belongsTo, references } from '@apexdesigner/schema-tools';

export const processInstanceSummary = z
  .object({
    id: z.uuid().hidden().describe('Unique identifier'),

    referenceNumber: z
      .number()
      .nullable()
      .optional()
      .describe('Sequential number shown to users'),

    name: z.string().nullable().optional().describe('Name of this instance'),

    status: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Current execution status.\n\nDeliberately a plain string rather than the ProcessInstanceStatus base\ntype: the search bar only builds conditions for string and text\nproperties, so a base-type-backed column is silently unsearchable. This\nview is read-only display, so it gives up nothing to type it loosely.',
      ),

    data: z
      .unknown()
      .nullable()
      .optional()
      .presentAs('json')
      .describe('Process data and variable values'),

    dataText: z
      .string()
      .nullable()
      .optional()
      .hidden()
      .describe(
        "The data payload's values flattened into a single searchable string.\nValues only — searching would otherwise match every instance whose\npayload merely has a property of that name.",
      ),

    candidateId: z
      .string()
      .nullable()
      .optional()
      .describe('Candidate id, promoted from the instance data payload'),

    candidateEmail: z
      .string()
      .nullable()
      .optional()
      .describe('Candidate email, promoted from the instance data payload'),

    errorMessage: z
      .string()
      .nullable()
      .optional()
      .describe('Explanation of the failure, when the instance has errored'),

    processName: z
      .string()
      .nullable()
      .optional()
      .describe('Internal name of the design'),

    processDisplayName: z
      .string()
      .nullable()
      .optional()
      .describe(
        'The design name as it is shown in lists — its display name, falling back\nto the internal name split on capitals. Computed here rather than in the\npage so that a search matches the text actually on screen.',
      ),

    processVersion: z
      .number()
      .nullable()
      .optional()
      .describe('Version number of the design this instance is running'),

    designUuid: z
      .string()
      .nullable()
      .optional()
      .hidden()
      .describe('Unique identifier for the design across all versions'),

    createdAt: z.coerce
      .date()
      .nullable()
      .optional()
      .describe('When this instance was started'),

    updatedAt: z.coerce
      .date()
      .nullable()
      .optional()
      .describe('When this instance was last modified'),

    completedAt: z.coerce
      .date()
      .nullable()
      .optional()
      .describe('When this instance finished'),

    processDesignId: z.uuid().hidden().describe('Foreign key to ProcessDesign'),

    tenantId: z
      .uuid()
      .nullable()
      .optional()
      .presentAs('reference')
      .describe('Foreign key to Tenant'),

    processDesign: belongsTo('ProcessDesign'),

    tenant: references('Tenant'),
  })
  .describe(
    "A read-only view of process instances joined to the design they run, for\nlist and search surfaces.\n\nReplaces the process-engine-library view for two reasons. It exposes the\ntenant partition column and applies the Tenant Scoped mixin, as every other\nview in this application does. And it promotes this application's own data\nproperties — the candidate the instance is about — out of the instance\n`data` JSON and into first-class columns, so the list can show and search\nthem in the database rather than in the browser.\n\n`candidateId` and `candidateEmail` are strings because the search bar only\nbuilds conditions for string properties; a typed column would be silently\nunsearchable. A design that declares neither leaves them empty — `->>`\nyields NULL for a missing key, so one view serves every process regardless\nof what it carries.",
  )
  .as('ProcessInstanceSummary');

export { processInstanceSummary as processInstanceSummarySchema };
